import { useEffect, useState } from 'preact/hooks';
import { useDeploymentSkin } from '../lib/skin';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { CaptureGlyph as Knot } from '../components/CaptureGlyph';
import {
  getEntryConfig,
  getSignupMode,
  ApiError,
  emailAuthMethods,
  requestMagicLink,
  requestBetaInvite,
  passkeyLoginOptions,
  passkeyLoginVerify,
  passwordLogin,
  type ApiUser,
  type EntryConfig,
} from '../lib/api';
import './Login.css';

export interface LoginProps {
  /** Set when App.tsx's magic-link-landing handling itself failed (an
   *  expired/already-used token) — shown once, above the form. */
  initialError?: string;
  /** Handed the freshly signed-in user (the passkey door; the magic-link
   *  door completes over in App.tsx's landing handler). App then runs
   *  tenant resolution (one -> Today, none -> onboarding, many -> picker). */
  onAuthenticated: (user: ApiUser, opts?: { justRegistered?: boolean }) => void;
}

// C6a (wf05 §01, blessed 2026-08-06): the front door opens honestly — ONE
// field, then the best door for that person. Email first; POST /auth/methods
// answers whether that account holds a passkey, and the ceremony is offered
// before anything else. Nobody is asked to choose an authentication method —
// that is a question about our implementation, not about them. {passkey:
// false} (which is ALSO what an unknown email gets — the probe is not an
// existence oracle) goes straight to the magic-link door, whose sent-state
// copy is already anti-enumerating ("if an account exists … a link is on its
// way"), and which covers brand-new accounts too (verify mints identity).
//
// The copy is PACK-DECLARED (GET /auth/entry) with neutral defaults — the
// Ether voice on a Rexx build, plain Vowric anywhere no pack speaks. The
// recovery path (troubleLine/reassurance) is on screen BEFORE anything goes
// wrong, and only when the pack actually declared one. This replaced the
// Auth-2 four-method wall (password/Google/Apple/passkey); the password
// ROUTES survive server-side (the door changed, not the locks).
//
// ⚑ `password` REJOINS THE UNION — 2026-09-20. Found live: a password was
// handed out (a demo account) and there was nowhere on this screen to type
// it, though POST /auth/password/login answered 200 the whole time. Added
// back as ONE MORE small door beside the other two, not a return to the old
// wall: a single "Use a password instead" link on the email stage, reached
// unconditionally (never probed for — the account-existence-oracle rule
// above is exactly why this is not gated on anything the server says about
// the address first). Magic link stays the default; passkey still wins when
// an account holds one. This is a considered exception to C6a (wf05 §01,
// blessed 2026-08-06), not a quiet reversal of it — flagged for Atlas to
// decide whether wf05 itself should be revised to say so.
// B1 (2026-09-24): 'betaRequest' (the request-stage's own email field) and
// 'betaRequestSent' (its receipt) — a second small door beside the
// existing four, same "one more stage, not a rebuild" posture the password
// door took on 2026-09-20.
type Stage = 'email' | 'passkey' | 'password' | 'sent' | 'paused' | 'betaRequest' | 'betaRequestSent';

// The web's own neutral voice — what a deployment whose pack declares no
// entry config (Actor, Personal) shows. Calm, no instructional captions.
const NEUTRAL_ENTRY: EntryConfig = {
  productName: 'Vowric',
  welcome: 'Let’s get you in.',
  emailLabel: 'Email',
};

export function Login({ initialError, onAuthenticated }: LoginProps) {
  // undefined = still fetching (render the quiet Knot beat, so the screen
  // never flashes the neutral voice at a Rexx tech for one frame).
  const [entry, setEntry] = useState<EntryConfig | undefined>(undefined);
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError ?? null);
  // B1: undefined while asking (never renders the link for one frame on a
  // beta deployment, same "quiet until known" posture `entry` itself
  // takes), then whatever GET /auth/entry actually says. A failed fetch
  // must never GROW a door that is not there — falls back to 'open'.
  const [signupMode, setSignupMode] = useState<'open' | 'beta' | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getSignupMode()
      .then((mode) => {
        if (!cancelled) setSignupMode(mode);
      })
      .catch(() => {
        if (!cancelled) setSignupMode('open');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getEntryConfig()
      .then((e) => {
        if (!cancelled) setEntry(e ?? NEUTRAL_ENTRY);
      })
      .catch(() => {
        // A failed copy fetch must never lock the door — speak neutrally.
        if (!cancelled) setEntry(NEUTRAL_ENTRY);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------------------------
  // THE DOOR WEARS THE DEPLOYMENT'S COLOURS. Added 2026-08-20, at the first
  // CallToWrap deployment, because the live sign-in screen rendered
  // CallToWrap's NAME in Vowric's GOLD — wordmark and primary button both —
  // while the pack's accent is rose-magenta (decision aed98b4d).
  //
  // That is decision ccd5851f one screen over: "the strip chip wears
  // --accent, not a fixed --gold". It was invisible to every assertion and
  // obvious in a screenshot, which is now the third time on this tranche.
  //
  // WHY THIS DOES NOT BREAK ce1c3046 ("the chassis is for operators"). That
  // decision keeps the operator CHASSIS off portal visitors — someone holding
  // a one-shot link who is not in your company. This screen is neither: it is
  // THIS DEPLOYMENT'S OWN FRONT DOOR, and it already speaks the pack's copy
  // from the same endpoint. Wearing the pack's colour is the same fact as
  // wearing its words. No chassis is mounted here and none is added.
  //
  // The vertical comes from `getDeploymentVertical()` — the deployment's own
  // packs[0].key, which exists precisely because the 2026-08-10 walk caught
  // signup hardcoding 'actors' onto a Rexx build. Never the stored tenant's
  // vertical: there is no tenant yet, which is the whole reason the skin was
  // missing here.
  //
  // B2 (2026-09-24): this screen's own effect became `lib/skin.ts`'s
  // `useDeploymentSkin()` — Onboarding and BetaInvite wear it the same way
  // now, so the three can never again drift on how a deployment's front
  // door gets dressed.
  useDeploymentSkin();

  // The magic-link door: request the link, then show the calm sent state.
  // The request path is always 202 server-side whether or not the account
  // exists (anti-enumeration). A 429 is DIFFERENT and safe to say out loud:
  // the email-keyed limiter fires at the same threshold for any address,
  // known or unknown, BEFORE the roster check — so naming it distinguishes
  // nothing, and staying silent tells a real family "a link is on its way"
  // four times while the server refused every send.
  async function sendLink(target: string) {
    let paused = false;
    try {
      await requestMagicLink(target);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) paused = true;
      /* anything else: same sent state — see above */
    }
    setBusy(false);
    setError(null);
    setStage(paused ? 'paused' : 'sent');
  }

  async function submitEmail() {
    const e = email.trim();
    if (!e || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { passkey } = await emailAuthMethods(e);
      if (passkey && browserSupportsWebAuthn()) {
        setBusy(false);
        setStage('passkey');
      } else {
        // No passkey on the account (or none this browser can drive), or an
        // unknown email — the SAME door either way, by design.
        await sendLink(e);
      }
    } catch {
      // A failed probe must never lock anyone out: the magic-link door is
      // the one that always works.
      await sendLink(e);
    }
  }

  async function doPasskey() {
    const e = email.trim();
    if (!e || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { ceremonyId, options } = await passkeyLoginOptions(e);
      const response = await startAuthentication({
        optionsJSON: options as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const user = await passkeyLoginVerify(ceremonyId, response);
      onAuthenticated(user);
    } catch {
      // Declined/failed/timed-out ceremony RETURNS CONTROL — the link door
      // below never went away (it is not disabled by this busy flag).
      setBusy(false);
      setError('That didn’t go through. The sign-in link below still works.');
    }
  }

  function backToEmail() {
    setStage('email');
    setBusy(false);
    setError(null);
  }

  // B1 — the request door. Same no-oracle posture as sendLink above: the
  // receipt is the same whatever the address, whatever the server actually
  // did with it (a fresh row, a repeat of one already pending, or — if
  // somehow reached on an 'open' deployment — nothing at all).
  async function submitBetaRequest() {
    const e = email.trim();
    if (!e || busy) return;
    setBusy(true);
    setError(null);
    try {
      await requestBetaInvite(e);
    } catch {
      // Same reasoning as sendLink's own catch: never lock the door.
    }
    setBusy(false);
    setStage('betaRequestSent');
  }

  // The password door — one more way in beside passkey/magic-link, never a
  // replacement for either. Not preceded by a probe: unlike the passkey
  // ceremony (offered only when the account holds one), this is reached the
  // same way for every address, which is what keeps it from becoming a
  // second account-existence oracle.
  async function doPasswordLogin() {
    const e = email.trim();
    if (!e || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const user = await passwordLogin(e, password);
      onAuthenticated(user);
    } catch (err) {
      // The route's own sentence, verbatim (voice canon: the server's own
      // words, never a vaguer second copy) — it is already the same generic
      // wording for a wrong password and an unknown email, so there is
      // nothing this screen could add without narrowing what it reveals.
      setBusy(false);
      setPassword('');
      setError(
        err instanceof ApiError && err.serverMessage
          ? err.serverMessage
          : 'That didn’t go through. The sign-in link below still works.'
      );
    }
  }

  // The pack's voice arrives before the door renders (see the entry state's
  // own comment) — until then, the same quiet beat App uses while booting.
  if (entry === undefined) {
    return (
      <div class="login">
        <div class="login__welcome">
          <Knot size={46} />
        </div>
      </div>
    );
  }

  const mark = (
    <div class="login__welcome">
      <Knot size={46} />
      <div class="login__eyebrow">
        {entry.productName ?? 'Vowric'}
        {entry.byline ? <span class="login__byline"> {entry.byline}</span> : null}
      </div>
      <div class="login__say">{entry.welcome}</div>
    </div>
  );

  // The recovery path, on screen BEFORE anything goes wrong — rendered only
  // when the pack actually declared one (no promise nobody made).
  const trouble =
    entry.troubleLine || entry.reassurance ? (
      <>
        <div class="login__divider">
          <span>trouble?</span>
        </div>
        <p class="login__trouble">
          {entry.troubleLine}
          {entry.troubleLine && entry.reassurance ? ' ' : ''}
          {entry.reassurance}
        </p>
      </>
    ) : null;

  const recordNotice = entry.recordNotice ? <p class="login__legal">{entry.recordNotice}</p> : null;

  if (stage === 'paused') {
    return (
      <div class="login">
        <div class="login__welcome">
          <Knot size={46} />
          <div class="login__eyebrow">A few links already went out</div>
          <div class="login__say">
            Several sign-in links were asked for
            <br />
            <b class="login__strong">{email.trim()}</b>
            <br />
            <span class="login__say-soft">
              this hour, so this one was not sent — the pause protects the address. The inbox has
              anything already sent; another link can go out in a little while.
            </span>
          </div>
        </div>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {recordNotice}
      </div>
    );
  }

  if (stage === 'betaRequestSent') {
    return (
      <div class="login">
        <div class="login__welcome">
          <Knot size={46} />
          <div class="login__eyebrow">Request received</div>
          <div class="login__say">
            When a spot opens, the invite goes to
            <br />
            <b class="login__strong">{email.trim()}</b>.
          </div>
        </div>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {recordNotice}
      </div>
    );
  }

  if (stage === 'betaRequest') {
    return (
      <div class="login">
        {mark}
        {error ? (
          <div class="login__error" role="alert">
            {error}
          </div>
        ) : null}
        <label class="login__label" for="login-email">
          {entry.emailLabel ?? 'Email'}
        </label>
        <input
          id="login-email"
          class="login__input"
          type="email"
          inputMode="email"
          autocomplete="email"
          placeholder="you@example.com"
          value={email}
          disabled={busy}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submitBetaRequest();
          }}
        />
        <button
          type="button"
          class="login__btn"
          disabled={busy || !email.trim()}
          onClick={() => void submitBetaRequest()}
        >
          {busy ? 'One moment…' : 'Request a beta invite'}
        </button>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {recordNotice}
      </div>
    );
  }

  if (stage === 'sent') {
    return (
      <div class="login">
        <div class="login__welcome">
          <Knot size={46} />
          <div class="login__eyebrow">Check your inbox</div>
          <div class="login__say">
            If an account exists for
            <br />
            <b class="login__strong">{email.trim()}</b>,<br />
            <span class="login__say-soft">a sign-in link is on its way.</span>
          </div>
        </div>
        {/* A mail app that opens a link inside its own in-app browser signs a
            person in somewhere they cannot get back to — not this screen's
            doing, and not fully fixable from here, so it is said plainly
            rather than worked around. Reassurance about what the LINK does,
            not an instruction about our UI, which is why it sits here rather
            than as a caption on a control. */}
        <p class="login__say-soft login__mailtip">
          Opens best in your regular browser — some mail apps show it inside themselves first. Look for
          “Open in Browser” if that happens.
        </p>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {recordNotice}
      </div>
    );
  }

  if (stage === 'passkey') {
    return (
      <div class="login">
        {mark}
        {error ? (
          <div class="login__error" role="alert">
            {error}
          </div>
        ) : null}
        <div class="login__whom">{email.trim()}</div>
        <button type="button" class="login__btn" disabled={busy} onClick={() => void doPasskey()}>
          {busy ? 'Waiting for your passkey…' : 'Use your passkey'}
        </button>
        {/* NEVER disabled — a ceremony that hangs must not close the other
            door (the C6a skippability invariant: no control state may trap
            the user). Same rule as the onboarding skip affordances. */}
        <button
          type="button"
          class="login__btn login__btn--ghost"
          onClick={() => void sendLink(email.trim())}
        >
          Send me a sign-in link instead
        </button>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {trouble}
        {recordNotice}
      </div>
    );
  }

  if (stage === 'password') {
    return (
      <div class="login">
        {mark}
        {error ? (
          <div class="login__error" role="alert">
            {error}
          </div>
        ) : null}
        <div class="login__whom">{email.trim()}</div>
        <label class="login__label" for="login-password">
          Password
        </label>
        <input
          id="login-password"
          class="login__input"
          type="password"
          autocomplete="current-password"
          value={password}
          disabled={busy}
          onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void doPasswordLogin();
          }}
        />
        <button
          type="button"
          class="login__btn"
          disabled={busy || !password}
          onClick={() => void doPasswordLogin()}
        >
          {busy ? 'One moment…' : 'Sign in'}
        </button>
        {/* NEVER disabled — same skippability invariant the passkey stage
            holds: a wrong or forgotten password must not trap anyone behind
            it when the link door still works. */}
        <button
          type="button"
          class="login__btn login__btn--ghost"
          onClick={() => void sendLink(email.trim())}
        >
          Send me a sign-in link instead
        </button>
        <button type="button" class="login__textlink" onClick={backToEmail}>
          &lsaquo; Use a different email
        </button>
        {trouble}
        {recordNotice}
      </div>
    );
  }

  // stage === 'email'
  return (
    <div class="login">
      {mark}
      {error ? (
        <div class="login__error" role="alert">
          {error}
        </div>
      ) : null}
      <label class="login__label" for="login-email">
        {entry.emailLabel ?? 'Email'}
      </label>
      <input
        id="login-email"
        class="login__input"
        type="email"
        inputMode="email"
        autocomplete="email"
        placeholder="you@example.com"
        value={email}
        disabled={busy}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submitEmail();
        }}
      />
      <button
        type="button"
        class="login__btn"
        disabled={busy || !email.trim()}
        onClick={() => void submitEmail()}
      >
        {busy ? 'One moment…' : 'Continue'}
      </button>
      {/* The password door — always here, for whoever was handed one (a demo
          account, an invite), never probed for first. Text-link weight, not
          a button: the funnel above is still email-first, and this is the
          one exception to it rather than a second front door of equal
          weight. */}
      <button
        type="button"
        class="login__textlink"
        disabled={busy || !email.trim()}
        onClick={() => setStage('password')}
      >
        Use a password instead
      </button>
      {/* B1: renders ONLY when GET /auth/entry says 'beta' — an 'open'
          deployment (every build before this chunk, and every build that
          has not set the `signup.mode` lever) shows nothing here, byte for
          byte as before. Never hardcoded to a product name — see
          getSignupMode's own header. */}
      {signupMode === 'beta' ? (
        <button
          type="button"
          class="login__textlink"
          disabled={busy}
          onClick={() => {
            setError(null);
            setStage('betaRequest');
          }}
        >
          New to {entry.productName ?? 'CallToWrap'}? Request a beta invite
        </button>
      ) : null}
      {trouble}
      {recordNotice}
    </div>
  );
}
